import MUIAlert, { AlertProps as MUIAlertProps, AlertClassKey } from '@mui/material/Alert';
import MUIAlertTitle from '@mui/material/AlertTitle';
import { makeStyles } from 'tss-react/mui';
import { CheckCircle, Info, Error, WarningSharp } from '@mui/icons-material';
import { UpliftThemeProvider } from '../theme/withTheme';
import { ClassNameMap } from '@mui/material';
import React from 'react';

export type AlertProps = Omit<MUIAlertProps, 'classes' | 'title'> & {
  classes?: Partial<ClassNameMap<AlertClassKey | 'title'>>;
  title?: React.ReactNode;
  showIcon?: boolean;
  flat?: boolean;
};

export const alertIconMapping: MUIAlertProps['iconMapping'] = {
  success: <CheckCircle />,
  info: <Info />,
  warning: <WarningSharp />,
  error: <Error />,
};

const useStyles = makeStyles<AlertProps, 'icon'>()((theme, _params, classes) => ({
  root: {
    borderWidth: '1px',
    borderStyle: 'solid',
    color: theme.palette.text.primary,
    alignItems: 'center',
  },
  flat: {
    borderRadius: 0,
    border: 'none',
  },
  icon: {
    display: 'none',
  },
  standardSuccess: {
    borderColor: theme.palette.success.main,
  },
  standardInfo: {
    borderColor: theme.palette.info.main,
  },
  standardWarning: {
    borderColor: theme.palette.warning.main,
  },
  standardError: {
    borderColor: theme.palette.error.main,
  },
  title: {
    fontWeight: 600,
  },
  showIcon: {
    [`&.${classes.icon}`]: {
      display: 'flex',
    }
  },
}));

export const Alert = React.forwardRef<HTMLDivElement, React.PropsWithChildren<AlertProps>>(({ children, title = null, showIcon = true, flat = false, ...props }, ref) => {
  const {
    cx,
    classes: {
      title: titleClassName,
      showIcon: showIconClassName,
      flat: flatClassName,
      ...classes
    }
  } = useStyles(props, { props });

  return (
    <UpliftThemeProvider>
      <MUIAlert
        ref={ref}
        {...props}
        classes={{
          ...classes,
          root: cx(classes.root, {
            [flatClassName]: flat,
          }),
          icon: cx(classes.icon, {
            [showIconClassName]: showIcon,
          })
        }}
        variant="standard"
        iconMapping={alertIconMapping}
      >
        {title && <MUIAlertTitle className={titleClassName}>{title}</MUIAlertTitle>}
        {children}
      </MUIAlert>
    </UpliftThemeProvider>
  );
});
